import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Check, CreditCard, ArrowLeft, ArrowRight, FileSignature, GraduationCap, BadgeCheck, Users, ChevronDown, ArrowRightLeft, Tag } from 'lucide-react';
import { PaymentMethodSelector, ZelleInstructionsPanel } from '../components/PaymentMethodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICES = { early_bird: 1390, normal: 1495, trimestral: 620 };

function ChildPlanSelector({ child, childIndex, totalChildren, plan, onPlanChange, language, methodDiscountPct = 0, methodLabel = '', couponDiscount = 0, couponCode = '', onCouponChange, onApplyCoupon, couponApplied = false }) {
  const isSibling = childIndex > 0;
  const basePrice = PRICES[plan.pricing_tier] || PRICES.normal;
  const siblingDiscount = isSibling ? Math.round(basePrice * 0.20) : 0;
  const afterSibling = basePrice - siblingDiscount;
  const evalCredit = child.evaluation_fee_amount || 52;
  const afterCredit = Math.max(0, afterSibling - evalCredit);
  const methodDiscount = methodDiscountPct > 0 ? Math.round(afterCredit * methodDiscountPct / 100 * 100) / 100 : 0;
  const appliedCoupon = (plan.pricing_tier !== 'trimestral' && couponApplied) ? couponDiscount : 0;
  const subtotal = Math.round((afterCredit - methodDiscount - appliedCoupon) * 100) / 100;

  const getInstallmentInfo = () => {
    if (plan.payment_plan === 'full' || plan.payment_plan === 'trimestral_3x') return null;
    if (plan.payment_plan === 'two_installments') {
      const each = Math.round(subtotal / 2 * 100) / 100;
      return { first: each, rest: subtotal - each, label: language === 'es' ? '2ª cuota' : '2nd installment' };
    }
    if (plan.payment_plan === 'monthly') {
      const each = Math.round(subtotal / 10 * 100) / 100;
      return { first: each, rest: each, label: language === 'es' ? 'Meses 2–10' : 'Months 2–10' };
    }
    return null;
  };

  const installment = getInstallmentInfo();
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-black/5 rounded-2xl overflow-hidden" data-testid={`child-plan-${childIndex}`}>
      {/* Child header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-[#007AFF]/5 hover:bg-[#007AFF]/8 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#E63946]/10 flex items-center justify-center text-sm font-semibold text-[#E63946]">
            {childIndex + 1}
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm text-[#1D1D1F]">{child.student_name}</p>
            <p className="text-xs text-[#007AFF]">{child.assigned_level}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-[#1D1D1F]">${subtotal.toLocaleString()}</span>
          <ChevronDown className={`h-4 w-4 text-[#86868B] transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="p-5 space-y-5">
          {/* Payment Plan: Pago único or Trimestral 3x */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#86868B]">{language === 'es' ? 'Plan de pago' : 'Payment Plan'}</Label>
            <div className="grid grid-cols-2 gap-3">
              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  plan.pricing_tier !== 'trimestral' ? 'border-[#E63946] bg-[#E63946]/5' : 'border-black/5 hover:border-black/20'
                }`}
                onClick={() => onPlanChange({ pricing_tier: 'normal', payment_plan: 'full' })}
                data-testid={`child-${childIndex}-plan-full`}
              >
                <p className="text-sm font-semibold text-[#1D1D1F]">{language === 'es' ? 'Pago único' : 'Full payment'}</p>
                <p className="text-xl font-bold text-[#E63946] mt-1">${PRICES.normal.toLocaleString()}</p>
                <p className="text-xs text-[#86868B] mt-1">{language === 'es' ? 'Año completo' : 'Full year'}</p>
              </div>
              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  plan.pricing_tier === 'trimestral' ? 'border-[#007AFF] bg-[#007AFF]/5' : 'border-black/5 hover:border-black/20'
                }`}
                onClick={() => onPlanChange({ pricing_tier: 'trimestral', payment_plan: 'trimestral_3x' })}
                data-testid={`child-${childIndex}-plan-trimestral`}
              >
                <p className="text-sm font-semibold text-[#1D1D1F]">{language === 'es' ? 'Trimestral (3 cuotas)' : 'Trimester (3 payments)'}</p>
                <p className="text-xl font-bold text-[#007AFF] mt-1">3 × $620</p>
                <p className="text-xs text-[#86868B] mt-1">{language === 'es' ? 'Total: $1,860' : 'Total: $1,860'}</p>
              </div>
            </div>
          </div>

          {/* Coupon field — only for pago único (first child only) */}
          {plan.pricing_tier !== 'trimestral' && childIndex === 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-xs font-medium text-[#86868B]">
                <Tag className="h-3 w-3 text-[#E63946]" />
                {language === 'es' ? 'Código de descuento' : 'Discount code'}
              </Label>
              <div className="flex gap-2">
                <Input
                  value={couponCode}
                  onChange={(e) => onCouponChange && onCouponChange(e.target.value.toUpperCase())}
                  placeholder="ARCE100"
                  className="py-4 rounded-xl uppercase font-mono text-sm"
                  data-testid={`child-${childIndex}-coupon-input`}
                />
                <Button
                  type="button"
                  onClick={() => onApplyCoupon && onApplyCoupon()}
                  disabled={!couponCode?.trim() || couponApplied}
                  className={`rounded-xl px-5 text-sm ${couponApplied ? 'bg-[#34C759] hover:bg-[#34C759]' : 'bg-[#E63946] hover:bg-[#D62828]'}`}
                  data-testid={`child-${childIndex}-apply-coupon`}
                >
                  {couponApplied ? <Check className="h-4 w-4" /> : (language === 'es' ? 'Aplicar' : 'Apply')}
                </Button>
              </div>
              {couponApplied && (
                <p className="text-xs text-[#34C759] font-medium">
                  {language === 'es' ? `Descuento aplicado: -$${couponDiscount}` : `Discount applied: -$${couponDiscount}`}
                </p>
              )}
            </div>
          )}

          {/* Price breakdown */}
          <div className="p-4 bg-[#F5F5F7] rounded-xl space-y-2 text-sm" data-testid={`child-${childIndex}-breakdown`}>
            <div className="flex justify-between">
              <span className="text-[#86868B]">{plan.pricing_tier === 'trimestral' ? (language === 'es' ? 'Trimestre 1 (T1)' : 'Trimester 1 (T1)') : (language === 'es' ? 'Matrícula anual' : 'Annual tuition')}</span>
              <span className="text-[#1D1D1F]">${basePrice.toLocaleString()}</span>
            </div>
            {isSibling && (
              <div className="flex justify-between">
                <span className="text-[#34C759]">{language === 'es' ? 'Descuento hermano 20%' : 'Sibling discount 20%'}</span>
                <span className="text-[#34C759] font-medium" data-testid={`child-${childIndex}-sibling-discount`}>-${siblingDiscount}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[#34C759]">{language === 'es' ? 'Crédito evaluación' : 'Assessment credit'}</span>
              <span className="text-[#34C759] font-medium" data-testid={`child-${childIndex}-eval-credit`}>-${evalCredit}</span>
            </div>
            {methodDiscount > 0 && (
              <div className="flex justify-between">
                <span className="text-[#6B21A8]">{language === 'es' ? `Descuento ${methodLabel}` : `${methodLabel} discount`}</span>
                <span className="text-[#6B21A8] font-medium" data-testid={`child-${childIndex}-method-discount`}>-${methodDiscount.toFixed(2)}</span>
              </div>
            )}
            {appliedCoupon > 0 && (
              <div className="flex justify-between">
                <span className="text-[#34C759]">{language === 'es' ? 'Cupón descuento' : 'Coupon discount'}</span>
                <span className="text-[#34C759] font-medium" data-testid={`child-${childIndex}-coupon-discount`}>-${appliedCoupon}</span>
              </div>
            )}
            <div className="border-t border-black/10 pt-2 flex justify-between">
              <span className="font-semibold text-[#1D1D1F]">Subtotal</span>
              <span className="font-bold text-[#1D1D1F]" data-testid={`child-${childIndex}-subtotal`}>${subtotal.toLocaleString()}</span>
            </div>
            {installment && (
              <>
                <div className="border-t border-black/5 pt-2 flex justify-between text-xs">
                  <span className="text-[#86868B]">{language === 'es' ? '1er pago' : '1st payment'}</span>
                  <span className="font-medium text-[#007AFF]">${installment.first.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#86868B]">{installment.label}</span>
                  <span className="font-medium text-[#1D1D1F]">${installment.rest.toFixed(2)} {language === 'es' ? 'c/u' : 'each'}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CompleteEnrollmentPage() {
  const { token } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const sigCanvas = useRef(null);

  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [enrollResult, setEnrollResult] = useState(null);

  // Per-child plans
  const [childPlans, setChildPlans] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [methodDiscountPct, setMethodDiscountPct] = useState(0);
  const [paymentSettings, setPaymentSettings] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponCode.trim(), plan_type: 'full' });
      if (res.data.valid) {
        setCouponApplied(true);
        setCouponDiscount(res.data.discount_amount);
        toast.success(language === 'es' ? `Cupón aplicado: -$${res.data.discount_amount}` : `Coupon applied: -$${res.data.discount_amount}`);
      }
    } catch (err) {
      setCouponApplied(false);
      setCouponDiscount(0);
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Cupón inválido' : 'Invalid coupon'));
    }
  };

  useEffect(() => { fetchData(); fetchPaySettings(); }, [token]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/evaluation-by-token/${token}`);
      const data = res.data;
      setFamilyData(data);

      const validChildren = (data.children || []).filter(c =>
        c.status === 'invitation_sent' || c.status === 'evaluated'
      );

      if (validChildren.length === 0) {
        toast.error(language === 'es' ? 'Esta invitación ya fue utilizada o no es válida.' : 'This invitation has already been used or is invalid.');
      }

      setChildPlans(validChildren.map(() => ({
        pricing_tier: 'early_bird',
        payment_plan: 'full'
      })));
    } catch (error) {
      toast.error(language === 'es' ? 'Invitación no encontrada' : 'Invitation not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaySettings = async () => {
    try {
      const res = await axios.get(`${API}/payment-settings/public`);
      setPaymentSettings(res.data);
    } catch (e) { /* fallback defaults */ }
  };

  const validChildren = (familyData?.children || []).filter(c =>
    c.status === 'invitation_sent' || c.status === 'evaluated'
  );

  const updateChildPlan = (idx, newPlan) => {
    setChildPlans(prev => prev.map((p, i) => i === idx ? newPlan : p));
  };

  // Calculate totals
  const calculateChildTotal = (childIdx) => {
    const child = validChildren[childIdx];
    const plan = childPlans[childIdx];
    if (!child || !plan) return { subtotal: 0, firstPayment: 0, methodDiscount: 0 };

    const base = PRICES[plan.pricing_tier] || PRICES.normal;
    const sibDiscount = childIdx > 0 ? Math.round(base * 0.20) : 0;
    const afterSib = base - sibDiscount;
    const credit = child.evaluation_fee_amount || 52;
    const afterCredit = Math.max(0, afterSib - credit);

    // Payment method discount
    const methodDiscount = methodDiscountPct > 0 ? Math.round(afterCredit * methodDiscountPct / 100 * 100) / 100 : 0;
    // Coupon discount (only for first child, pago único)
    const appliedCoupon = (childIdx === 0 && plan.pricing_tier !== 'trimestral' && couponApplied) ? couponDiscount : 0;
    const subtotal = Math.round((afterCredit - methodDiscount - appliedCoupon) * 100) / 100;

    let firstPayment = subtotal;
    if (plan.payment_plan === 'two_installments') firstPayment = Math.round(subtotal / 2 * 100) / 100;
    if (plan.payment_plan === 'monthly') firstPayment = Math.round(subtotal / 10 * 100) / 100;

    return { base, sibDiscount, credit, afterCredit, methodDiscount, appliedCoupon, subtotal, firstPayment };
  };

  const grandTotal = validChildren.reduce((sum, _, i) => sum + calculateChildTotal(i).firstPayment, 0);
  const grandSubtotal = validChildren.reduce((sum, _, i) => sum + calculateChildTotal(i).subtotal, 0);
  const grandSubtotalBeforeMethod = validChildren.reduce((sum, _, i) => sum + calculateChildTotal(i).afterCredit, 0);
  const methodLabel = paymentMethod === 'ach' ? 'ACH' : paymentMethod === 'zelle' ? 'Zelle' : '';

  const handleSubmit = async () => {
    if (!acceptedTerms) {
      toast.error(language === 'es' ? 'Debe aceptar las condiciones' : 'You must accept the terms');
      return;
    }
    if (!signerName || signerName.length < 3) {
      toast.error(language === 'es' ? 'Introduzca su nombre completo' : 'Enter your full name');
      return;
    }

    setSubmitting(true);
    try {
      let signatureData = '';
      if (sigCanvas.current) {
        try {
          signatureData = sigCanvas.current.toDataURL('image/png');
        } catch (e) {
          console.error('Signature capture error:', e);
        }
      }

      // Use the first child's evaluation_id as the API endpoint
      const firstEvalId = validChildren[0].evaluation_id;
      const childrenPayload = validChildren.map((child, i) => ({
        evaluation_id: child.evaluation_id,
        pricing_tier: childPlans[i].pricing_tier,
        payment_plan: childPlans[i].payment_plan
      }));

      const res = await axios.post(`${API}/evaluations/${firstEvalId}/complete-enrollment`, {
        children: childrenPayload,
        payment_method: paymentMethod,
        signer_name: signerName,
        signature_data: signatureData,
        accepted_terms: acceptedTerms,
        ...(couponApplied && { coupon_code: couponCode.trim() })
      });

      // If Stripe checkout URL returned, redirect to Stripe
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }

      setEnrollResult(res.data);
      setStep(3);
      toast.success(
        res.data.payment_method === 'zelle'
          ? (language === 'es' ? 'Inscripción registrada — pendiente pago Zelle' : 'Enrollment registered — pending Zelle payment')
          : (language === 'es' ? '¡Inscripción completada!' : 'Enrollment complete!')
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  if (!familyData || validChildren.length === 0) {
    return (
      <div className="min-h-screen bg-[#F5F5F7]">
        <Navbar />
        <main className="pt-40 pb-20 px-6">
          <div className="max-w-lg mx-auto card-apple p-8 text-center">
            <p className="text-[#86868B]">{language === 'es' ? 'Invitación no encontrada o ya utilizada.' : 'Invitation not found or already used.'}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isMulti = validChildren.length > 1;

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <SEOHead
        titleES="Finalizar Inscripción — Colegio ARCE"
        titleEN="Complete Enrollment — Colegio ARCE"
        descES="Completa tu inscripción en Colegio ARCE."
        descEN="Complete your enrollment at Colegio ARCE."
        path={`/enrollment/complete/${token}`}
        noindex
      />
      <main className="pt-40 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#1D1D1F] mb-3" data-testid="complete-enrollment-title">
              {language === 'es' ? 'Finalizar Inscripción' : 'Complete Enrollment'}
            </h1>
            {isMulti && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#AF52DE]/10 text-[#AF52DE] rounded-full text-sm font-medium">
                <Users className="h-4 w-4" />
                {validChildren.length} {language === 'es' ? 'alumnos' : 'students'}
              </div>
            )}
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-center mb-10">
            {[
              { icon: GraduationCap, label: language === 'es' ? 'Plan de pago' : 'Payment Plan' },
              { icon: FileSignature, label: language === 'es' ? 'Contrato' : 'Contract' },
              { icon: BadgeCheck, label: language === 'es' ? 'Confirmación' : 'Confirmation' }
            ].map((s, i) => (
              <div key={i} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step > i + 1 ? 'bg-[#34C759] text-white' :
                  step === i + 1 ? 'bg-[#E63946] text-white shadow-lg' : 'bg-black/5 text-[#86868B]'
                }`}>
                  {step > i + 1 ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </div>
                {i < 2 && <div className={`w-12 h-0.5 mx-1 ${step > i + 1 ? 'bg-[#34C759]' : 'bg-black/10'}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Plan Selection per child */}
          {step === 1 && (
            <div className="space-y-6" data-testid="complete-step-1">
              <h2 className="text-xl font-semibold text-[#1D1D1F]">
                {language === 'es'
                  ? (isMulti ? 'Selecciona un plan por alumno' : 'Selecciona tu plan de pago')
                  : (isMulti ? 'Select a plan per student' : 'Select your payment plan')}
              </h2>

              {/* Per-child plan selectors */}
              <div className="space-y-4">
                {validChildren.map((child, idx) => (
                  <ChildPlanSelector
                    key={child.evaluation_id}
                    child={child}
                    childIndex={idx}
                    totalChildren={validChildren.length}
                    plan={childPlans[idx] || { pricing_tier: 'early_bird', payment_plan: 'full' }}
                    onPlanChange={(p) => { updateChildPlan(idx, p); if (p.pricing_tier === 'trimestral') { setCouponApplied(false); setCouponDiscount(0); setCouponCode(''); } }}
                    language={language}
                    methodDiscountPct={methodDiscountPct}
                    methodLabel={methodLabel}
                    couponCode={couponCode}
                    couponApplied={couponApplied}
                    couponDiscount={couponDiscount}
                    onCouponChange={(v) => { setCouponCode(v); setCouponApplied(false); setCouponDiscount(0); }}
                    onApplyCoupon={applyCoupon}
                  />
                ))}
              </div>

              {/* Payment Method Selector */}
              <div className="card-apple p-6" data-testid="payment-method-section">
                <PaymentMethodSelector
                  amount={grandSubtotalBeforeMethod}
                  language={language}
                  onMethodChange={(method, pct) => {
                    setPaymentMethod(method);
                    setMethodDiscountPct(pct);
                  }}
                  showComparison={true}
                />
              </div>

              {/* Zelle Instructions */}
              {paymentMethod === 'zelle' && (
                <ZelleInstructionsPanel
                  amount={grandSubtotal}
                  studentNames={validChildren.map(c => c.student_name).join(', ')}
                  language={language}
                  settings={paymentSettings}
                />
              )}

              {/* Family Total */}
              <div className="p-6 bg-[#1D1D1F] rounded-2xl text-white" data-testid="complete-family-total">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/60">{language === 'es' ? 'Total familia' : 'Family Total'}</p>
                    {paymentMethod !== 'card' && (
                      <p className="text-xs text-[#34C759] mt-0.5">
                        {paymentMethod === 'ach' ? 'ACH' : 'Zelle'} — {language === 'es' ? `${methodDiscountPct}% descuento` : `${methodDiscountPct}% discount`}
                      </p>
                    )}
                    {grandTotal !== grandSubtotal && (
                      <p className="text-xs text-white/40">
                        {language === 'es' ? 'Primer pago' : 'First payment'} ({childPlans.some(p => p.payment_plan !== 'full')
                          ? (language === 'es' ? 'incluye cuotas' : 'includes installments')
                          : ''})
                      </p>
                    )}
                  </div>
                  <p className="text-4xl font-bold" data-testid="complete-grand-total">
                    ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </p>
                </div>
                {isMulti && (
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                    {validChildren.map((child, idx) => {
                      const calc = calculateChildTotal(idx);
                      return (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-white/60">{child.student_name}</span>
                          <span className="text-white font-medium">
                            ${calc.firstPayment.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            {idx > 0 && <span className="text-[#34C759] text-xs ml-1">(-20%)</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Contract */}
          {step === 2 && (
            <div className="card-apple p-8 space-y-6" data-testid="complete-step-2">
              <h2 className="text-xl font-semibold text-[#1D1D1F]">
                {language === 'es' ? 'Contrato de inscripción' : 'Enrollment Agreement'}
              </h2>

              {/* Children summary */}
              <div className="space-y-2">
                {validChildren.map((child, idx) => {
                  const calc = calculateChildTotal(idx);
                  const plan = childPlans[idx];
                  const tierLabel = { early_bird: 'Early Bird', normal: 'Normal', trimestral: 'Trimestral' }[plan?.pricing_tier] || '';
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl text-sm" data-testid={`contract-child-${idx}`}>
                      <div>
                        <span className="font-medium text-[#1D1D1F]">{child.student_name}</span>
                        <span className="text-[#86868B] ml-2">{child.assigned_level}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-[#1D1D1F]">${calc.subtotal}</span>
                        <span className="text-xs text-[#86868B] ml-1">({tierLabel})</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="max-h-48 overflow-y-auto p-4 bg-[#F5F5F7] rounded-xl text-sm text-[#86868B] leading-relaxed">
                {language === 'es'
                  ? 'Este contrato establece los términos y condiciones de inscripción en Colegio ARCE para el año escolar correspondiente. Al firmar, el padre/tutor acepta los términos de pago, las políticas de asistencia y las normas académicas del colegio. El plan de pago seleccionado es vinculante y el crédito de evaluación ha sido aplicado. Los pagos no son reembolsables una vez procesados.'
                  : 'This contract establishes the terms and conditions of enrollment at Colegio ARCE for the corresponding school year. By signing, the parent/guardian accepts the payment terms, attendance policies, and academic standards of the school. The selected payment plan is binding and the evaluation credit has been applied. Payments are non-refundable once processed.'}
              </div>

              <div className="flex items-start gap-3 p-4 bg-[#F5F5F7] rounded-xl">
                <Checkbox id="accept_complete" checked={acceptedTerms} onCheckedChange={(c) => setAcceptedTerms(c)} data-testid="complete-accept-terms" />
                <Label htmlFor="accept_complete" className="cursor-pointer text-sm leading-relaxed">
                  {language === 'es'
                    ? 'Acepto las condiciones de matrícula de Colegio ARCE.'
                    : 'I accept the enrollment terms of Colegio ARCE.'}
                </Label>
              </div>

              <div className="space-y-2">
                <Label>{language === 'es' ? 'Nombre completo del firmante' : 'Full Name of Signer'}</Label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder={familyData.parent_name}
                  className="py-6 rounded-xl"
                  data-testid="complete-signer-name"
                />
              </div>

              <div className="space-y-2">
                <Label>{language === 'es' ? 'Firma' : 'Signature'}</Label>
                <div className="border-2 border-dashed border-black/15 rounded-2xl bg-white overflow-hidden relative">
                  <SignatureCanvas ref={sigCanvas} penColor="#1D1D1F" canvasProps={{ width: 660, height: 180, className: 'w-full h-[180px]', 'data-testid': 'complete-signature-canvas' }} />
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-[#86868B]/50 pointer-events-none">
                    {language === 'es' ? 'Dibuje su firma aquí' : 'Draw your signature here'}
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => sigCanvas.current?.clear()} className="rounded-lg text-xs">
                  {language === 'es' ? 'Borrar firma' : 'Clear signature'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="card-apple p-8 text-center space-y-6 py-12" data-testid="complete-step-3">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${enrollResult?.payment_method === 'zelle' ? 'bg-[#FF9500]' : 'bg-[#34C759]'}`}>
                {enrollResult?.payment_method === 'zelle'
                  ? <ArrowRightLeft className="h-10 w-10 text-white" />
                  : <Check className="h-10 w-10 text-white" />}
              </div>
              <h2 className="text-2xl font-semibold text-[#1D1D1F]" data-testid="enrollment-success-title">
                {enrollResult?.payment_method === 'zelle'
                  ? (language === 'es' ? 'Inscripción registrada — Pendiente de pago Zelle' : 'Enrollment registered — Pending Zelle payment')
                  : (language === 'es' ? '¡Inscripción completada!' : 'Enrollment Complete!')}
              </h2>

              {/* Enrolled children summary */}
              <div className="max-w-md mx-auto space-y-3">
                {(enrollResult?.enrollments || validChildren).map((child, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl text-sm" data-testid={`enrolled-child-${idx}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${enrollResult?.payment_method === 'zelle' ? 'bg-[#FF9500]/20' : 'bg-[#34C759]/20'}`}>
                        {enrollResult?.payment_method === 'zelle'
                          ? <ArrowRightLeft className="h-3 w-3 text-[#FF9500]" />
                          : <Check className="h-3 w-3 text-[#34C759]" />}
                      </div>
                      <span className="font-medium text-[#1D1D1F]">{child.student_name}</span>
                    </div>
                    <span className="text-xs text-[#007AFF]">{child.assigned_level || child.pricing_tier}</span>
                  </div>
                ))}
                {enrollResult && (
                  <div className="p-4 bg-[#1D1D1F] rounded-xl flex justify-between items-center">
                    <span className="text-white/60 text-sm">
                      {enrollResult.payment_method === 'zelle'
                        ? (language === 'es' ? 'Total a pagar por Zelle' : 'Total to pay via Zelle')
                        : (language === 'es' ? 'Total pagado' : 'Total paid')}
                    </span>
                    <span className="text-xl font-bold text-white" data-testid="confirmation-total">${enrollResult.grand_total}</span>
                  </div>
                )}
              </div>

              <p className="text-[#86868B] max-w-md mx-auto text-sm">
                {enrollResult?.payment_method === 'zelle'
                  ? (language === 'es'
                    ? 'Hemos enviado un correo con las instrucciones de pago Zelle. Tu inscripción será confirmada en 24–48h tras la verificación del pago.'
                    : "We've sent an email with Zelle payment instructions. Your enrollment will be confirmed within 24-48h after payment verification.")
                  : (language === 'es'
                    ? 'Recibirás un correo con los detalles de tu inscripción. Te contactaremos con los horarios y materiales antes del inicio de clases.'
                    : "You'll receive an email with your enrollment details. We'll contact you with schedules and materials before classes begin.")}
              </p>

              <Button onClick={() => navigate('/')} className="rounded-xl px-8 bg-[#E63946] hover:bg-[#D62828]" data-testid="complete-go-home">
                {language === 'es' ? 'Volver al inicio' : 'Back to Home'}
              </Button>
            </div>
          )}

          {/* Navigation */}
          {step < 3 && (
            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={() => setStep(prev => prev - 1)}
                disabled={step === 1}
                className="rounded-xl px-6"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Atrás' : 'Back'}
              </Button>
              {step === 1 ? (
                <Button
                  onClick={() => setStep(2)}
                  className="rounded-xl px-6 bg-[#E63946] hover:bg-[#D62828]"
                  data-testid="complete-next-btn"
                >
                  {language === 'es' ? 'Siguiente' : 'Next'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`rounded-xl px-6 ${paymentMethod === 'zelle' ? 'bg-[#6B21A8] hover:bg-[#5B1A98]' : 'bg-[#34C759] hover:bg-[#2DB24C]'}`}
                  data-testid="complete-submit-btn"
                >
                  {paymentMethod === 'zelle'
                    ? <ArrowRightLeft className="h-4 w-4 mr-2" />
                    : <CreditCard className="h-4 w-4 mr-2" />}
                  {submitting
                    ? '...'
                    : paymentMethod === 'zelle'
                      ? (language === 'es' ? `Confirmar pago por Zelle $${grandSubtotal.toLocaleString()}` : `Confirm Zelle payment $${grandSubtotal.toLocaleString()}`)
                      : (language === 'es' ? `Pagar $${grandTotal.toLocaleString()}` : `Pay $${grandTotal.toLocaleString()}`)}
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
